import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Switch,
  Typography,
  Card,
  CardContent,
  Button,
  Container,
  CircularProgress,
  Paper,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "../../../../components/MDBox";
import LogoutButton from "layouts/logout";
import { useNavigate } from "react-router-dom";
import api from "core/interceptors/api";
import DropDown from "layouts/tables/admin/service_controls/servicedropdown";
import Swal from "sweetalert2";

const ServiceControls = () => {
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState("AllServices");
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [changesMade, setChangesMade] = useState(false);

  const fetchServicePermissions = async (serviceEmail) => {
    if (serviceEmail === "AllServices") {
      setPermissions({});
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`/service_control/get_permissions?email=${serviceEmail}`);
      const fetchedPermissions = res.data.permissions || {};
      delete fetchedPermissions.email;
      setPermissions(fetchedPermissions);
      setChangesMade(false);
    } catch (error) {
      console.error("Error fetching service permissions:", error);
      setPermissions({});
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (field) => {
    setPermissions((prev) => ({ ...prev, [field]: !prev[field] }));
    setChangesMade(true);
  };

  const handleSave = async () => {
    if (!changesMade) return;
    try {
      await api.post("/service_control/update", {
        email: selectedService,
        ...permissions,
      });
      setChangesMade(false);
      Swal.fire({
        title: "Saved!",
        text: "Permissions updated successfully.",
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error updating permissions:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to update permissions.",
        icon: "error",
        timer: 2000,
        showConfirmButton: false,
      });
    }
  };

  useEffect(() => {
    if (selectedService !== "AllServices") {
      fetchServicePermissions(selectedService);
    }
  }, [selectedService]);

  return (
    <DashboardLayout>
      <MDBox display="flex" justifyContent="flex-end" alignItems="center">
        <LogoutButton onLogout={() => navigate("/login")} />
      </MDBox>
      <DashboardNavbar />

      <Container maxWidth="md" sx={{ mt: 3, mb: 10 }}>
        <Paper
          elevation={3}
          sx={{ p: 3, bgcolor: "#f3f9f7", borderRadius: 2 }}
        >
          <DropDown onSelectOption={setSelectedService} />

          {selectedService !== "AllServices" && (
            <>
              {loading ? (
                <Box textAlign="center" my={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Grid container spacing={1} mt={2}>
                  {["dashboard", "user_profile"].map((field) => (
                    <Grid item xs={12} sm={6} md={4} key={field}>
                      <Card
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          borderRadius: 1.5,
                          boxShadow: 1,
                          background: "#ffffff",
                          minHeight: 50,
                        }}
                      >
                        <CardContent
                          sx={{
                            p: 4,
                            flexGrow: 0,
                            flexShrink: 1,
                            flexBasis: "70%",
                            maxWidth: "220px",
                            overflow: "hidden",
                            textOverflow: "ellipsis", // Add ellipsis for long text
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.7rem",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            fontWeight="medium"
                          >
                            {field.replace(/_/g, " ").toUpperCase()}
                          </Typography>
                        </CardContent>
                        <Switch
                          checked={Boolean(permissions[field])}
                          onChange={() => handleToggle(field)}
                          size="small"
                          sx={{
                            transform: "scale(0.8)",
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#00C9A7",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: "#00C9A7",
                            },
                          }}
                        />
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </>
          )}
        </Paper>

        {selectedService !== "AllServices" && (
          // Changed position to sticky and width to 100%
          <Box
            sx={{
              position: "sticky", // Changed from "fixed"
              bottom: 0,
              width: "100%", // Ensures it fills the container width
              backgroundColor: "#fff",
              borderTop: "1px solid #eee",
              py: 1.5,
              display: "flex",
              justifyContent: "center",
              zIndex: 999,
              mt: 3, // Add some top margin for spacing
              borderRadius: 2 // Match parent Paper's border-radius
            }}
          >
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!changesMade}
              sx={{
                px: 3,
                py: 0.8,
                fontSize: "0.75rem",
                borderRadius: 2,
                backgroundColor: changesMade ? "#1976d2" : "#ccc",
                color: "white !important",
                opacity: changesMade ? 1 : 0.7,
                boxShadow: 1,
                "&:hover": {
                  backgroundColor: changesMade ? "#1565c0" : "#ccc",
                },
              }}
            >
              SAVE
            </Button>
          </Box>
        )}
      </Container>
    </DashboardLayout>
  );
};

export default ServiceControls;